import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import Footer from '../../Components/Footer';
import ProductCard from '../../Components/ProductCard';
import './ProductDetail.css';
import CustomizedCake from '../../Components/CustomizedCake';
import Navbar from './Navbar';
import AnimatedNumber from '../../Components/AnimatedNumber';
import { useCart } from '../../Components/CartContext';
import { useNotification } from '../../Components/NotificationContext';
import { fetchProductById, fetchProductsByCategoryId } from '../../data/catalog';
import { STATIC_IMAGES } from '../../data/staticData';
import { trackViewItem } from '../../lib/analytics';


function ProductDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showContactForm, setShowContactForm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });


  // Cart and notification hooks
  const { addToCart, cart } = useCart();
  const { showNotification } = useNotification();


  const processImageUrl = (imageUrl) => {
    if (!imageUrl) return STATIC_IMAGES.product;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
    if (imageUrl.startsWith('/')) return imageUrl;
    return `/${imageUrl}`;
  };

  const getDefaultVariant = (categoryName) => {
    const c = categoryName?.toLowerCase() || '';
    if (c.includes('cake')) return { weight: '1kg', price: 800 };
    if (c.includes('cookie')) return { weight: 'Box of 6', price: 400 };
    return { weight: '1kg', price: 800 };
  };

  const [{ product, relatedProducts, loading, error }, setState] = useState({
    product: null, relatedProducts: [], loading: true, error: null,
  });

  useEffect(() => {
    let active = true;
    if (!id) { setState({ product: null, relatedProducts: [], loading: false, error: null }); return; }
    setState(s => ({ ...s, loading: true }));
    (async () => {
      const raw = await fetchProductById(id);
      if (!active) return;
      if (!raw) { setState({ product: null, relatedProducts: [], loading: false, error: 'Product not found' }); return; }
      const defaultVariant = getDefaultVariant(raw.Category?.Name);
      const transformedProduct = {
        id: raw._id,
        name: raw.productName,
        tagline: raw.Tagline,
        description: raw.ProductDesc,
        description2: "",
        benefits: raw.Benefits,
        images: Array.isArray(raw.Images) && raw.Images.length > 0
          ? raw.Images.map(img => processImageUrl(img))
          : [STATIC_IMAGES.product],
        variants: Array.isArray(raw.variants) && raw.variants.length > 0
          ? raw.variants.map(v => ({ weight: v.weight, price: v.price }))
          : [defaultVariant],
        ingredients: raw.Ingredients,
        delivery: "24 hours notice needed for pickups",
        category: raw.Category?.Name?.toLowerCase() || "products",
        issoldout: raw.isSoldOut
      };
      const related = (await fetchProductsByCategoryId(raw.Category?._id))
        .filter(p => p._id !== id)
        .slice(0, 3)
        .map(p => ({
          id: p._id,
          name: p.productName,
          tagline: p.Tagline,
          description: p.ProductDesc,
          price: p.variants?.[0]?.price ?? 0,
          weight: p.variants?.[0]?.weight ?? 'N/A',
          image: processImageUrl(p.Images?.[0] || null),
          ingredients: p.Ingredients,
          issoldout: p.isSoldOut
        }));
      if (active) setState({ product: transformedProduct, relatedProducts: related, loading: false, error: null });
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (product?.variants?.length > 0) setSelectedVariant(product.variants[0]);
  }, [product?.id]);

  // GA4 view_item, once per product actually loaded.
  useEffect(() => {
    if (!product?.id) return;
    trackViewItem({
      id: product.id,
      name: product.name,
      category: product.category,
      price: product.variants?.[0]?.price ?? product.price,
    });
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSoldOut = product?.issoldout ?? false;


  if (loading) {
    return (
      <div className="loading-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.5rem'
      }}>
        Loading...
      </div>
    );
  }


  if (error || !product) {
    return (
      <div className="error-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.5rem',
        color: 'red'
      }}>
        {error || 'Product not found'}
      </div>
    );
  }


  const currentPrice = selectedVariant?.price || (product.variants && product.variants.length > 0 ? product.variants[0].price : getDefaultVariant(product.category).price);
  const currentWeight = selectedVariant?.weight || (product.variants && product.variants.length > 0 ? product.variants[0].weight : getDefaultVariant(product.category).weight);


  const handleVariantChange = (variantIndex) => {
    if (product.variants && product.variants[variantIndex]) {
      setSelectedVariant(product.variants[variantIndex]);
    }
  };


  const handleQuantityChange = (type) => {
    if (type === 'increase') {
      setQuantity(prev => prev + 1);
    } else if (type === 'decrease' && quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };


  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };


  const handleAddToCart = () => {
    console.log('Before addToCart - Current cart:', cart);
    
    // Create product object in the format expected by cart with variant information
    const productForCart = {
      id: product.id,
      name: product.name,
      price: currentPrice,
      image: product.images[0],
      variant: currentWeight, // Using selected variant's weight as variant
      variantPrice: currentPrice, // Store the variant-specific price
      ingredients: product.ingredients,
      issoldout: product.issoldout
    };
    
    console.log('Adding product with variant:', productForCart, 'Quantity:', quantity); 
    
    addToCart(productForCart, quantity);
    showNotification(`${product.name} (${currentWeight}) added to cart!`);
    
    setTimeout(() => {
      console.log('After addToCart - Cart should be updated'); 
    }, 100);
  };


  const handleContactSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    // Here you would typically send the form data to your backend
    setShowContactForm(false);
    setFormData({ name: '', email: '', message: '' });
  };


  const handleImageChange = (index) => {
    setCurrentImageIndex(index);
  };


  const handleRelatedProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };


  const pageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };


  const slideVariants = {
    enter: {
      opacity: 0,
      scale: 0,
      x: 0,
      y: 0
    },
    center: {
      opacity: 1,
      scale: 1,
      x: 0,
      y: 0
    },
    exit: {
      opacity: 0,
      scale: 1.5,
      x: 0,
      y: 0
    }
  };


  return (
    <>
    <Navbar/>
      <div className="product-detail-wrapper">
        <motion.div 
          className="product-detail-page"
          variants={pageVariants}
          initial="hidden"
          animate="visible"
        >
      
      <div className="product-detail-container">
        {/* Back Button */}
       
        <div className="product-detail-content">
          {/* Product Images */}
          <motion.div 
            className="product-images"
            variants={imageVariants}
          >
            <div className="main-image-container">
              <AnimatePresence>
                {product.images && product.images.length > 0 ? (
                  <motion.img
                    key={currentImageIndex}
                    src={product.images[currentImageIndex]}
                    alt={product.name}
                    className="main-image"
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      duration: 0.3,
                      ease: "easeOut"
                    }}
                    onError={(e) => {
                      console.error(`Failed to load main image: ${product.images[currentImageIndex]}`);
                      console.error('Product images array:', product.images);
                      console.error('Current image index:', currentImageIndex);
                      e.target.src = STATIC_IMAGES.product;
                    }}
                    onLoad={() => {
                      console.log(`Successfully loaded main image: ${product.images[currentImageIndex]}`);
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <img
                    src={STATIC_IMAGES.product}
                    alt={product.name}
                    className="main-image"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                )}
              </AnimatePresence>
            </div>
            
            {/* Image Thumbnails */}
            {product.images.length > 1 && (
              <div className="image-thumbnails">
                {product.images.map((image, index) => (
                  <motion.div
                    key={index}
                    className={`thumbnail ${currentImageIndex === index ? 'active' : ''}`}
                    onClick={() => handleImageChange(index)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img 
                      src={image} 
                      alt={`${product.name} ${index + 1}`}
                      onError={(e) => {
                        console.error(`Failed to load thumbnail image: ${image}`);
                        e.target.src = STATIC_IMAGES.product;
                      }}
                      onLoad={() => {
                        console.log(`Successfully loaded thumbnail image: ${image}`);
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>


          {/* Product Information */}
          <div className="product-info-section">
            <motion.div 
              className="product-header"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="product-title">{product.name}</h1>
              {product.tagline && <p className="product-tagline">{product.tagline}</p>}
            </motion.div>

            <motion.p 
              className="product-description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {product.description}
            </motion.p>


            
           
            {/* Product Information */}
            

            <div className="price-display">
                <span className="current-price">£{typeof currentPrice === 'number' ? currentPrice.toFixed(2) : currentPrice}</span>
                
              </div>


            {/* Size/Weight Selection */}
            <motion.div 
              className="size-selection"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h3>Select Variant :</h3>
              <div className="size-dropdown">
                <select 
                  value={product.variants.findIndex(v => v.weight === currentWeight)}
                  onChange={(e) => handleVariantChange(parseInt(e.target.value))}
                  className="size-select"
                >
                  {product.variants.map((variant, index) => (
                    <option key={index} value={index}>
                      {variant.weight}
                    </option>
                  ))}
                </select>
              </div>
            </motion.div>


            {/* Price and Quantity */}
            <motion.div 
              className="price-quantity-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <div className="quantity-section">
                <h3 style={{fontSize: "1.2rem"}}>Quantity :</h3>
                <div className="quantity-controls">
                  <motion.button
                    className="quantity-btn decrease"
                    onClick={() => handleQuantityChange('decrease')}
                    disabled={quantity === 1}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    −
                  </motion.button>
                  <AnimatedNumber value={quantity} />
                  <motion.button
                    className="quantity-btn increase"
                    onClick={() => handleQuantityChange('increase')}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    +
                  </motion.button>
                </div>
              </div>
            </motion.div>


            {/* Action Buttons */}
            <motion.div 
              className="action-buttons"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <motion.button
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSoldOut}
              >
                {isSoldOut ? 'SOLD OUT' : `ADD TO CART `}
              </motion.button>
            </motion.div>


            

            <motion.div>
              <p>*Taxes and delivery charges calculated at checkout</p>
            </motion.div>


            <motion.div 
              className="product-features"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              {product.ingredients && Array.isArray(product.ingredients) && product.ingredients.length > 0 ? (
                product.ingredients.slice(0, 3).map((ingredient, index) => (
                  <div key={index} className="feature-item">
                    
                    <span className="feature-text">{ingredient.toUpperCase()}</span>
                  </div>
                ))
              ) : (
                // Fallback to default ingredients if no ingredients are available
                <>
                  <div className="feature-item">
                    <img src="/1.png" alt="Buttercream" className="feature-icon" />
                    <span className="feature-text">BUTTERCREAM</span>
                  </div>
                  <div className="feature-item">
                    <img src="/2.png" alt="3 Layers" className="feature-icon" />
                    <span className="feature-text">3 LAYERS</span>
                  </div>
                  <div className="feature-item">
                    <img src="/3.png" alt="Real Vanilla" className="feature-icon" />
                    <span className="feature-text">REAL VANILLA</span>
                  </div>
                </>
              )}
            </motion.div>

            {/* Product Features Icons */}
            <motion.div 
              className="product-info-list"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="info-item">
                <span className="info-icon"></span>
                <span>*Manufactured in a facility that also handles Peanuts, Soy, Milk,  Tree nuts, Eggs, Wheat, Sesame and may contain traces of the same.</span>
              </div>
              
              
            </motion.div>
            
          </div>
        </div>


        {/* You May Also Like Section */}
        <motion.div 
          className="related-products-section"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <h2 className="related-title">YOU MAY ALSO LIKE:</h2>
          <div className="related-products-grid">
            {relatedProducts.map((relatedProduct, index) => (
              <motion.div
                key={relatedProduct.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 + index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <ProductCard cookie={relatedProduct} />
              </motion.div>
            ))}
          </div>
        </motion.div>
        <CustomizedCake />
      </div>


      {/* Contact Form Modal */}
      {showContactForm && (
        <motion.div 
          className="contact-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className="contact-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
          >
            <div className="modal-header">
              <h3>Ask About {product.name}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowContactForm(false)}
              >
                ×
              </button>
            </div>


            <form onSubmit={handleContactSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
              </div>


              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                />
              </div>


              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  placeholder="Tell us about your requirements, special requests, or any questions..."
                  rows="4"
                  required
                />
              </div>


              <div className="form-actions">
                <motion.button
                  type="submit"
                  className="submit-btn"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Send Message
                </motion.button>
                <motion.button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowContactForm(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
        </motion.div>
      </div>
      <Footer />
    </>
  );
}


export default ProductDetail;
